import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { cars, conditions, carBrands, bodyTypes, fuelTypes } from "../mock";
import CarCard from "../components/CarCard";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const maxPrice = Math.max(...cars.map((c) => c.price));

export default function CarList() {
  const [params, setParams] = useSearchParams();
  const [condition, setCondition] = useState(params.get("condition") || "Tutti");
  const [brand, setBrand] = useState(params.get("brand") || "Tutte le marche");
  const [body, setBody] = useState(params.get("body") || "Tutte");
  const [fuel, setFuel] = useState("Tutti");
  const [price, setPrice] = useState([0, maxPrice]);
  const [sort, setSort] = useState("price-asc");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setCondition(params.get("condition") || "Tutti");
    setBrand(params.get("brand") || "Tutte le marche");
    setBody(params.get("body") || "Tutte");
  }, [params]);

  const filtered = useMemo(() => {
    let list = cars.filter((c) => {
      if (condition !== "Tutti" && c.condition !== condition) return false;
      if (brand !== "Tutte le marche" && c.brand !== brand) return false;
      if (body !== "Tutte" && c.body !== body) return false;
      if (fuel !== "Tutti" && c.fuel !== fuel) return false;
      if (c.price < price[0] || c.price > price[1]) return false;
      return true;
    });
    switch (sort) {
      case "price-asc": list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "km-asc": list.sort((a, b) => a.km - b.km); break;
      case "year-desc": list.sort((a, b) => b.year - a.year); break;
      default: break;
    }
    return list;
  }, [condition, brand, body, fuel, price, sort]);

  const reset = () => {
    setCondition("Tutti"); setBrand("Tutte le marche"); setBody("Tutte");
    setFuel("Tutti"); setPrice([0, maxPrice]); setParams({});
  };

  const FilterControls = () => (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-semibold text-[#16181d] mb-2 block">Condizione</label>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{conditions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-semibold text-[#16181d] mb-2 block">Marca</label>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{carBrands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-semibold text-[#16181d] mb-2 block">Carrozzeria</label>
        <Select value={body} onValueChange={setBody}>
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{bodyTypes.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-semibold text-[#16181d] mb-2 block">Alimentazione</label>
        <Select value={fuel} onValueChange={setFuel}>
          <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{fuelTypes.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-semibold text-[#16181d] mb-2 block">
          Prezzo: € {price[0].toLocaleString("it-IT")} — € {price[1].toLocaleString("it-IT")}
        </label>
        <Slider min={0} max={maxPrice} step={500} value={price} onValueChange={setPrice} className="mt-3" />
      </div>
      <Button variant="outline" onClick={reset} className="w-full rounded-xl">Azzera filtri</Button>
    </div>
  );

  return (
    <main className="pt-24 pb-20 bg-[#f5f6f8] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#16181d]">Le nostre auto</h1>
          <p className="text-gray-500 mt-2">{filtered.length} veicoli disponibili</p>
        </div>

        <div className="flex items-center justify-between gap-3 mb-6">
          <Button variant="outline" className="lg:hidden rounded-xl" onClick={() => setShowFilters(true)}>
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Filtri
          </Button>
          <div className="ml-auto">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-11 w-56"><SelectValue placeholder="Ordina" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="price-asc">Prezzo crescente</SelectItem>
                <SelectItem value="price-desc">Prezzo decrescente</SelectItem>
                <SelectItem value="km-asc">Km crescenti</SelectItem>
                <SelectItem value="year-desc">Più recenti</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-24">
              <h3 className="font-display font-bold text-lg mb-5">Filtra</h3>
              <FilterControls />
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <p className="text-gray-500">Nessuna auto corrisponde ai filtri selezionati.</p>
                <Button onClick={reset} className="mt-4 rounded-full bg-[#e2001a] hover:bg-[#c00016]">Azzera filtri</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((car) => <CarCard key={car.id} car={car} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white p-6 overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-lg">Filtra</h3>
              <button onClick={() => setShowFilters(false)}><X className="w-5 h-5" /></button>
            </div>
            <FilterControls />
            <Button onClick={() => setShowFilters(false)} className="w-full mt-5 rounded-xl bg-[#e2001a] hover:bg-[#c00016]">
              Mostra {filtered.length} auto
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
